import { IconButton, LabelChip, Modal, type DataTableColumn } from "@carbon6/c6-frontend-design";
import { useId, useState, type ReactNode } from "react";
import { HatModalHeader } from "@/components/modal";
import { HatTextTooltip } from "@/components/tooltip";
import { TrainingSessionRefLink } from "../TrainingSessionRefLink";
import type { ScheduleOccurrenceTableRow } from "./ScheduleOccurrenceTableRow";
import { occurrenceStatusChipColor } from "./scheduleFormat";
import styles from "./ScheduleOccurrencesWindowContent.module.css";

// 실행 이력 창의 열. 자격·상태와 무관한 고정 상수다. 예정 시각이 정렬 축이다(상류가 최신순으로 준다).
export const SCHEDULE_OCCURRENCE_COLUMNS: readonly DataTableColumn<ScheduleOccurrenceTableRow>[] = [
  {
    key: "scheduledFor",
    header: "예정 시각",
    width: "minmax(170px, 200px)",
    render: (row) => <span className={styles.timeText}>{row.scheduledFor}</span>,
  },
  {
    // skipped 는 실패가 아니라 branch 가 바뀌지 않은 정상 회차다. 그래서 상태 어휘를 그대로 쓴다.
    key: "statusLabel",
    header: "결과",
    width: "96px",
    render: (row) => (
      <LabelChip
        color={occurrenceStatusChipColor(row.occurrence.status)}
        size="xs"
        variant="filled"
      >
        {row.statusLabel}
      </LabelChip>
    ),
  },
  {
    // 실행이 만든 Session. 실패·건너뜀·실행 전이면 없다. 응답에 이름이 없으므로 id 앞부분으로
    // 표시하고, 창을 열면 상세가 제목을 바꾼다.
    key: "sessionId",
    header: "Session",
    width: "minmax(160px, 1fr)",
    render: (row) => <SessionCell sessionId={row.sessionId} />,
  },
  {
    key: "sha",
    header: "확인 Commit",
    width: "110px",
    render: (row) => (
      <span className={styles.monoText} title={row.occurrence.resolved_sha ?? undefined}>
        {row.sha}
      </span>
    ),
  },
  {
    // 상류가 남긴 설명. 건너뜀 사유와 실패 로그가 같은 칸(`error`)에 온다. 실패만 붉은색이다.
    // 긴 로그는 한 줄에 다 들어가지 않으므로 [자세히]가 전문 모달을 연다.
    key: "error",
    header: "상태 설명",
    width: "minmax(240px, 2fr)",
    render: (row) => <StatusDescriptionCell row={row} />,
  },
];

// 이 길이를 넘거나 여러 줄이면 툴팁으로 읽기 어렵다. 그래서 [자세히]가 전문 모달을 연다.
export const OCCURRENCE_LOG_DETAIL_THRESHOLD = 80;

export function needsOccurrenceLogDetail(text: string): boolean {
  return text.length > OCCURRENCE_LOG_DETAIL_THRESHOLD || text.includes("\n");
}

function SessionCell({ sessionId }: { sessionId: string | null }): ReactNode {
  if (sessionId === null) {
    return <span className={styles.subText}>—</span>;
  }
  return (
    <TrainingSessionRefLink sessionId={sessionId} displayName={sessionDisplayLabel(sessionId)} />
  );
}

// occurrence 응답에는 Session 이름이 없다. 그래서 id 앞 8자로 표시한다.
export function sessionDisplayLabel(sessionId: string): string {
  return `Session ${sessionId.slice(0, 8)}`;
}

// 상태 설명 칸. 건너뜀은 정상 회차의 사유라 중립색이고, 실패는 붉은 로그다. 실패 로그가 길면
// [자세히]가 전문 모달을 연다(툴팁은 한 문장까지만 읽기 좋다).
function StatusDescriptionCell({ row }: { row: ScheduleOccurrenceTableRow }): ReactNode {
  const text = row.error;
  const failed = row.occurrence.status === "failed";
  if (text === "") {
    return <span className={styles.subText}>—</span>;
  }
  const line = (
    <HatTextTooltip text={text}>
      <span className={failed ? styles.failureText : styles.noteText}>{text}</span>
    </HatTextTooltip>
  );
  if (!failed || !needsOccurrenceLogDetail(text)) {
    return line;
  }
  return (
    <div className={styles.descriptionCell}>
      {line}
      <OccurrenceLogDetail row={row} />
    </div>
  );
}

// [자세히] 버튼과 전문 모달. 로그는 줄바꿈과 공백을 그대로 유지하고(pre-wrap) 모두 표시한다.
function OccurrenceLogDetail({ row }: { row: ScheduleOccurrenceTableRow }): ReactNode {
  const [open, setOpen] = useState(false);
  const titleId = useId();
  return (
    <>
      <IconButton text="자세히" size="sm" variant="ghost" onClick={() => setOpen(true)} />
      <Modal open={open} onClose={() => setOpen(false)} size="md" aria-labelledby={titleId}>
        <HatModalHeader title="실패 로그" titleId={titleId} close />
        <Modal.Body>
          <p className={styles.logMeta}>{row.scheduledFor} 실행</p>
          <pre className={styles.logText}>{row.error}</pre>
        </Modal.Body>
        <Modal.Footer>
          <IconButton text="닫기" size="sm" variant="surface" onClick={() => setOpen(false)} />
        </Modal.Footer>
      </Modal>
    </>
  );
}
